import {
  Alert,
  Card,
  Collapse,
  DatePicker,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { fetchCrowsNestOllieFindings } from 'src/utils/crowsNestApi'
import { formatFunnelScopeLabel } from 'src/utils/crowsNestFunnel'
import {
  CROWS_NEST_OLLIE_DEFAULT_RANGE_DAYS,
  CROWS_NEST_OLLIE_PLAN_FILTERS,
  buildOllieFindingsQuery,
  isOllieTableOverflowing,
  mapCrowsNestOllieFindings,
  ollieFindingsPlanLabel,
  splitOllieFindingsByActivity,
} from 'src/utils/crowsNestOllieFindings'
import { CROWS_NEST_TOOLTIPS } from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

/** Too early has no colour: the plain tag is the readable neutral. */
const STATUS_COLOURS = Object.freeze({
  needs_work: 'red',
  helping: 'green',
})

/** People (or venues / fixes) per check, like the columns; raw events on hover. */
function HeadlineTotal({ stat, testId }) {
  return (
    <>
      <TotalLabel>{stat.label}</TotalLabel>
      <Tooltip title={stat.eventsText}>
        <TotalValue data-testid={testId} data-events={stat.events ?? undefined}>
          {stat.value}
          <TotalUnit> {stat.unitWord}</TotalUnit>
        </TotalValue>
      </Tooltip>
    </>
  )
}

const { Option } = Select
const { RangePicker } = DatePicker
const { Panel } = Collapse

const DAY_FORMAT = 'YYYY-MM-DD'

const byNumber = pick => (a, b) => (pick(a) ?? -1) - (pick(b) ?? -1)

const lastDays = days => [
  moment()
    .subtract(days - 1, 'days')
    .startOf('day'),
  moment().endOf('day'),
]

const RANGE_PRESETS = Object.freeze({
  'Last 7 days': lastDays(7),
  'Last 30 days': lastDays(30),
  'Last 90 days': lastDays(90),
})

/**
 * Ollie findings by check — which checks help venues, which need work,
 * which are too noisy.
 */
export { STATUS_COLOURS as CROWS_NEST_OLLIE_STATUS_COLOURS }

/** True when the table body is wider than the card (re-measured on resize). */
export const useTableOverflow = (ref, rows) => {
  const [overflowing, setOverflowing] = useState(false)
  useEffect(() => {
    const measure = () => {
      const body = ref.current?.querySelector?.('.ant-table-body')
      setOverflowing(isOllieTableOverflowing(body))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [ref, rows])
  return overflowing
}

const CrowsNestOllieFindings = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [range, setRange] = useState(() =>
    lastDays(CROWS_NEST_OLLIE_DEFAULT_RANGE_DAYS),
  )
  const [plan, setPlan] = useState('')

  const fromDate = range?.[0] ? range[0].format(DAY_FORMAT) : undefined
  const toDate = range?.[1] ? range[1].format(DAY_FORMAT) : undefined

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchCrowsNestOllieFindings(
          buildOllieFindingsQuery({ fromDate, toDate, plan }),
        )
        if (!cancelled) setSummary(mapCrowsNestOllieFindings(response))
      } catch (err) {
        if (!cancelled) {
          setError(
            crowsNestErrorMessage(
              err,
              "Couldn't load Ollie findings. Try again in a moment.",
            ),
          )
          setSummary(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [fromDate, toDate, plan])

  const columns = useMemo(
    () => [
      {
        title: 'Check',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_value, row) => (
          <Stack>
            <span>{row.name}</span>
            <Sample>
              {row.checkId}
              {row.enabled ? '' : ' · off'}
            </Sample>
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Status'
            tip={CROWS_NEST_TOOLTIPS.ollieStatus}
          />
        ),
        key: 'status',
        render: (_value, row) => (
          <Stack>
            <span>
              <Tag
                color={STATUS_COLOURS[row.status]}
                data-testid='ollie-findings-status'
                data-status={row.status}
              >
                {row.statusLabel}
              </Tag>
            </span>
            {row.reason ? <Sample>{row.reason}</Sample> : null}
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Found at'
            tip={CROWS_NEST_TOOLTIPS.ollieFound}
          />
        ),
        key: 'found',
        sorter: byNumber(row => row.found.venues),
        render: (_value, row) => row.foundLabel,
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Shown to'
            tip={CROWS_NEST_TOOLTIPS.ollieShown}
          />
        ),
        key: 'shown',
        sorter: byNumber(row => row.shown.people),
        render: (_value, row) => row.shownLabel,
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Snoozed'
            tip={CROWS_NEST_TOOLTIPS.ollieSnoozed}
          />
        ),
        key: 'snoozed',
        sorter: byNumber(row => row.snoozedRate),
        render: (_value, row) => (
          <Stack
            data-testid='ollie-findings-snoozed'
            data-noisy={String(row.noisy)}
          >
            <span>{row.snoozedLabel}</span>
            <Sample>
              {row.noisy ? (
                <Tag color='red'>{row.snoozedRateLabel} of people shown</Tag>
              ) : (
                `${row.snoozedRateLabel} of people shown`
              )}
            </Sample>
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Acted on'
            tip={CROWS_NEST_TOOLTIPS.ollieActedOn}
          />
        ),
        key: 'actedOn',
        sorter: byNumber(row => row.actedOnRate),
        render: (_value, row) => (
          <Stack data-testid='ollie-findings-acted-on'>
            <span>{row.actedOnLabel}</span>
            <Sample>{row.actedOnRateLabel} of people shown</Sample>
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Fixed'
            tip={CROWS_NEST_TOOLTIPS.ollieFixed}
          />
        ),
        key: 'fixed',
        sorter: byNumber(row => row.fixed.events),
        render: (_value, row) => (
          <Stack>
            <span>{row.fixedLabel}</span>
            {row.fixedDetail ? <Sample>{row.fixedDetail}</Sample> : null}
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Time to fix'
            tip={CROWS_NEST_TOOLTIPS.ollieTimeToFix}
          />
        ),
        key: 'timeToFix',
        sorter: byNumber(row => row.timeToFix.medianDays),
        render: (_value, row) => (
          <Stack>
            <span>{row.timeToFixLabel}</span>
            <Sample>{row.timeToFixSample}</Sample>
          </Stack>
        ),
      },
      {
        title: (
          <CrowsNestTipLabel
            label='Reported wrong'
            tip={CROWS_NEST_TOOLTIPS.ollieReportedWrong}
          />
        ),
        key: 'reportedWrong',
        sorter: byNumber(row => row.reportedWrongRate),
        render: (_value, row) => (
          <Stack data-testid='ollie-findings-reported-wrong'>
            <span>{row.reportedWrongLabel}</span>
            <Sample>{row.reportedWrongRateLabel} of people shown</Sample>
            {row.alreadySortedLabel ? (
              <Sample data-testid='ollie-findings-already-sorted'>
                {row.alreadySortedLabel}
              </Sample>
            ) : null}
          </Stack>
        ),
      },
    ],
    [],
  )

  const totals = summary?.totals
  const scopeLabel = formatFunnelScopeLabel(
    summary?.range,
    ollieFindingsPlanLabel(summary?.filters?.plan),
  )
  const { active, quiet } = useMemo(
    () => splitOllieFindingsByActivity(summary?.byCheck || []),
    [summary],
  )
  const tableRef = useRef(null)
  const overflowing = useTableOverflow(tableRef, active)

  return (
    <StatsCard
      data-testid='crows-nest-ollie-findings'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Ollie findings by check</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.ollieFindingsSection}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {summary ? (
            <ScopeLabel data-testid='ollie-findings-scope-label'>
              {scopeLabel}
            </ScopeLabel>
          ) : null}
        </TitleBlock>
      }
      extra={
        <FilterRow>
          <span data-testid='ollie-findings-range-filter'>
            <RangePicker
              value={range}
              onChange={value => setRange(value?.length ? value : null)}
              ranges={RANGE_PRESETS}
              format='D MMM YYYY'
              allowClear={false}
              disabledDate={day => day && day.isAfter(moment().endOf('day'))}
              style={{ width: 250 }}
            />
          </span>
          <span data-testid='ollie-findings-plan-filter'>
            <Select value={plan} onChange={setPlan} style={{ width: 140 }}>
              {CROWS_NEST_OLLIE_PLAN_FILTERS.map(option => (
                <Option key={option.value || 'all-plans'} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </span>
        </FilterRow>
      }
    >
      {error && !summary ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !summary ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {totals ? (
        <>
          <TotalsRow>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.found}
                testId='ollie-findings-total-found'
              />
            </TotalStat>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.shown}
                testId='ollie-findings-total-shown'
              />
            </TotalStat>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.snoozed}
                testId='ollie-findings-total-snoozed'
              />
            </TotalStat>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.actedOn}
                testId='ollie-findings-total-acted-on'
              />
            </TotalStat>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.fixed}
                testId='ollie-findings-total-fixed'
              />
            </TotalStat>
            <TotalStat>
              <TotalLabel>Time to fix</TotalLabel>
              <TotalValue data-testid='ollie-findings-total-time'>
                {totals.timeToFixLabel}
              </TotalValue>
              <TotalSample>{totals.timeToFixSample}</TotalSample>
            </TotalStat>
            <TotalStat>
              <HeadlineTotal
                stat={totals.headline.reportedWrong}
                testId='ollie-findings-total-wrong'
              />
              {totals.alreadySorted ? (
                <TotalSample data-testid='ollie-findings-total-already-sorted'>
                  {totals.alreadySorted} already sorted (not counted)
                </TotalSample>
              ) : null}
            </TotalStat>
            <TotalStat>
              <TotalLabel>Checks needing work</TotalLabel>
              <TotalValue data-testid='ollie-findings-total-needs-work'>
                {totals.needsWork}
              </TotalValue>
              <TotalSample>{totals.helping} helping</TotalSample>
            </TotalStat>
          </TotalsRow>

          <div ref={tableRef}>
            <Table
              rowKey='key'
              columns={columns}
              dataSource={active}
              loading={loading}
              pagination={false}
              size='middle'
              locale={{
                emptyText: summary.byCheck.length
                  ? 'No check had any activity in this range.'
                  : 'No Ollie checks registered.',
              }}
              scroll={{ x: true }}
            />
          </div>
          {overflowing ? (
            <ScrollHint data-testid='ollie-findings-scroll-hint'>
              Scroll the table sideways for every column.
            </ScrollHint>
          ) : null}

          {quiet.length ? (
            <QuietCollapse bordered={false}>
              <Panel
                key='quiet'
                header={
                  <span data-testid='ollie-findings-quiet-header'>
                    No activity yet ({quiet.length})
                  </span>
                }
              >
                <QuietList data-testid='ollie-findings-quiet-list'>
                  {quiet.map(row => (
                    <li key={row.key}>
                      <span>{row.name}</span>
                      <Sample>
                        {row.checkId}
                        {row.enabled ? '' : ' · off'}
                      </Sample>
                    </li>
                  ))}
                </QuietList>
              </Panel>
            </QuietCollapse>
          ) : null}
        </>
      ) : null}
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  .ant-card-body {
    min-width: 0;
  }

  /* macOS hides overlay scrollbars; the table must look scrollable. */
  .ant-table-body {
    scrollbar-color: ${({ theme }) => theme.colours.oliveGrey || '#999'}
      transparent;
    scrollbar-width: thin;
  }

  .ant-table-body::-webkit-scrollbar {
    height: 8px;
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colours.oliveGrey || '#999'};
    border-radius: 4px;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const FilterRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const QuietCollapse = styled(Collapse)`
  margin-top: ${({ theme }) => theme.spacing.md || '16px'};
`

const QuietList = styled.ul`
  display: grid;
  gap: 8px 24px;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`

const ScopeLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const TotalsRow = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const TotalStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TotalLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 13px;
  font-weight: 500;
`

const TotalValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
`

const TotalUnit = styled.span`
  font-size: 15px;
  font-weight: 500;
`

const TotalSample = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ScrollHint = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 11px;
  margin-top: 4px;
`

const Sample = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 11px;
`

export default CrowsNestOllieFindings
